import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { injectIntl, intlShape } from 'react-intl';
import styled from 'styled-components';

import { COLOR_ROOMS_RED } from '../constants/colors';
import { breakpoints } from '../constants/mediaqueries';
import { fontStyles } from '../constants/styles';
import { customMarkdown } from '../helpers/markdown';
import DirectBookingGallery from '../components/DirectBookingGallery';
import DirectBookingPicker from '../components/DirectBookingPicker';
import DirectBookingResults from '../components/DirectBookingResults';
import GradientWrapper from '../components/GradientWrapper';
import HelmetMichelberger from '../components/HelmetMichelberger';
import Layout from '../components/Layout';
import PageTitle from '../components/PageTitle';


// Softened variant of the rooms-page red gradient, used when the Book Direct
// Page record leaves its own gradient empty. This page carries more
// light-weight body copy than the rooms page, and the fully saturated rooms
// gradient makes that thin type hard to read; these stops keep the rooms-red
// identity but lift lightness so black copy sits more comfortably. The accent
// colour still tracks the CMS rooms gradient, so the CTA/results salmon is
// unchanged.
const HERO_GRADIENT = ['#f5789a', '#ff7d6f', '#ff8e6d'].map((hex) => ({ color: { hex } }));

/* One responsive hero: the intro stacks above the swipeable gallery until the
   xl breakpoint, where it becomes a two-column grid with the gallery beside the
   copy. The split only kicks in at xl (not l) because at 1024-1439 the gallery
   column is narrow enough to go near-square, and iPad Pro portrait sits exactly
   on 1024; those widths read better with the full-width stacked gallery. */
const HeroRow = styled.div`
  margin: 1.5rem 0 0;

  @media ${breakpoints.xl} {
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: 3rem;
    /* Top-align both columns: the gallery carries its own viewport-scaled
       height, so the copy reads from the upper left instead of floating centred
       beside a taller image. */
    align-items: start;
    margin: 1rem 0 1.5rem;
  }
`;

const Intro = styled.div`
  ${fontStyles.body}

  p { margin: 0 0 1em; }
  p:last-child { margin-bottom: 0; }
`;

/* scroll-margin-top clears the fixed header so the smooth scroll-on-search
   lands the picker just below it, not behind it. We scroll to the picker (not
   the results) so the chosen dates stay in view after a search; the results
   render right below and the guest scrolls down to reach them. */
const PickerAnchor = styled.div`
  scroll-margin-top: 5rem;
`;


// Everything on this page that the hotel edits comes from the Book Direct
// Page record in Dato (title, intro, hero gallery, gradient, SEO) and the
// Book Direct Messaging record (labels, registered as bookDirect.* messages
// in AppLayout). The rooms and the accent colour come from the rooms page, as
// on /rooms.
const BookDirectPage = ({ data, intl, location, pageContext }) => {
  const { languageSwitch } = pageContext;
  const footerData = data.allDatoCmsFooter.edges[0].node;
  const roomsPage = data.allDatoCmsRoomsPage.edges[0].node;
  const { gradient: roomsGradient, rooms, slug: roomsSlug } = roomsPage;
  const buttonColor = roomsGradient.length ? roomsGradient[0].color.hex : COLOR_ROOMS_RED;

  const page = data.allDatoCmsBookDirectPage.edges[0].node;
  const {
    description, gallery, gradient, name, seoMetaTags,
  } = page;
  const pageGradient = gradient.length ? gradient : HERO_GRADIENT;
  // Vector assets have no fluid data and cannot be a hero slide.
  const galleryImages = gallery
    .filter((image) => image.fluid)
    .map((image) => ({ id: image.url, alt: image.alt, fluid: image.fluid }));

  const pickerRef = React.useRef(null);
  const scrollToPicker = () => {
    const node = pickerRef.current;
    if (!node) return;
    // Defer a frame so the results have begun rendering their searching state
    // below before we scroll, giving the motion a settled target.
    window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <Layout hideBookingLink languageSwitch={languageSwitch} location={location} pageContext={pageContext}>
      <GradientWrapper footerData={footerData} gradient={pageGradient} languageSwitch={languageSwitch}>
        <HelmetMichelberger seo={seoMetaTags} />
        <PageTitle>{name}</PageTitle>
        <HeroRow>
          <Intro>{customMarkdown(description)}</Intro>
          <DirectBookingGallery
            ariaLabel={intl.formatMessage({ id: 'bookDirect.galleryLabel', defaultMessage: 'Michelberger impressions' })}
            images={galleryImages}
          />
        </HeroRow>
        <PickerAnchor innerRef={pickerRef}>
          <DirectBookingPicker buttonColor={buttonColor} onSearch={scrollToPicker} />
        </PickerAnchor>
        <DirectBookingResults buttonColor={buttonColor} rooms={rooms} roomsSlug={roomsSlug} />
      </GradientWrapper>
    </Layout>
  );
};

BookDirectPage.propTypes = {
  data: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  location: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
};

export default injectIntl(BookDirectPage);

export const query = graphql`
  query BookDirectPage($locale: String!) {
    allDatoCmsBookDirectPage(filter: { locale: { eq: $locale } }) {
      edges {
        node {
          name
          description
          gradient {
            color {
              hex
            }
          }
          gallery {
            alt
            url
            fluid(maxWidth: 2400, maxHeight: 1600, imgixParams: { auto: "format" }) {
              ...GatsbyDatoCmsFluid
            }
          }
          seoMetaTags {
            ...GatsbyDatoCmsSeoMetaTags
          }
        }
      }
    }
    allDatoCmsRoomsPage(filter: { locale: { eq: $locale } }) {
      edges {
        node {
          slug
          gradient {
            color {
              hex
            }
          }
          rooms {
            id
            slug
            name
            bookingId
            details
            featuredImage {
              alt
              mimeType
              url
              fluid(maxWidth: 2400, maxHeight: 1600, imgixParams: { auto: "format" }) {
                ...GatsbyDatoCmsFluid
              }
            }
          }
        }
      }
    }
    allDatoCmsFooter(filter: { locale: { eq: $locale } }) {
      edges {
        node {
          ...footerFields
        }
      }
    }
  }
`;
